import { EntityType } from '@/plugins/cad/module/CADModule/model/common/constant'
import type { ILine, IPoint } from '@/plugins/cad/module/CADModule/model/common/interface'
import { BaseEntity } from './BaseEntity'

/** 多重引线的内容承载类型，当前仅支持多行文字 */
export type MLeaderContentType = 'mtext'

/** 多重引线分支 */
export interface MLeaderBranch {
  /** 箭头所在点 */
  arrowPoint: IPoint
  /** 分支折点列表，首个为肘点，次个为着陆终点 */
  vertices: IPoint[]
}

/** 引线着陆方向，1 表示向右，-1 表示向左 */
export type MLeaderLandingDirection = 1 | -1

interface NormalizeMLeaderBranchOptions {
  textPosition?: IPoint
  landingDistance?: number
  hasLanding?: boolean
}

/** 多重引线默认着陆距离，单位米 */
export const DEFAULT_MLEADER_LANDING_DISTANCE = 1

const MLEADER_TEXT_GAP_FACTOR = 0.35
const MLEADER_EPSILON = 1e-6

function clonePoint(point: IPoint): IPoint {
  return { x: point.x, y: point.y }
}

function isSamePoint(a?: IPoint, b?: IPoint) {
  if (!a || !b) {
    return false
  }
  return Math.abs(a.x - b.x) <= MLEADER_EPSILON && Math.abs(a.y - b.y) <= MLEADER_EPSILON
}

function resolveLandingDirection(
  startPoint: IPoint,
  elbowPoint: IPoint,
  textPosition?: IPoint
): MLeaderLandingDirection {
  if (textPosition && textPosition.x !== elbowPoint.x) {
    return textPosition.x >= elbowPoint.x ? 1 : -1
  }
  if (elbowPoint.x !== startPoint.x) {
    return elbowPoint.x >= startPoint.x ? 1 : -1
  }
  return 1
}

function normalizeMLeaderBranch(
  branch: MLeaderBranch,
  options?: NormalizeMLeaderBranchOptions
): MLeaderBranch {
  const arrowPoint = branch.arrowPoint ? clonePoint(branch.arrowPoint) : { x: 0, y: 0 }
  const vertices = (branch.vertices || []).map(clonePoint)
  const elbowPoint = vertices[0]
    ? clonePoint(vertices[0])
    : options?.textPosition
      ? clonePoint(options.textPosition)
      : clonePoint(arrowPoint)

  const normalizedVertices = [elbowPoint]
  if (options?.hasLanding !== false) {
    const landingEnd = vertices[1]
      ? clonePoint(vertices[1])
      : buildMLeaderLandingEnd(elbowPoint, {
          direction: resolveLandingDirection(arrowPoint, elbowPoint, options?.textPosition),
          landingDistance: options?.landingDistance,
          textPosition: options?.textPosition,
        })
    normalizedVertices.push(landingEnd)
  }

  return {
    arrowPoint,
    vertices: normalizedVertices,
  }
}

/**
 * 获取默认着陆距离。
 *
 * @returns 默认着陆距离，单位米
 */
export function getDefaultMLeaderLandingDistance(_textHeight?: number, _landingGap?: number) {
  return DEFAULT_MLEADER_LANDING_DISTANCE
}

/**
 * 获取文字与着陆线端点之间的可视间距。
 *
 * @param textHeight - 文字高度
 * @param landingGap - 实体记录的着陆间距
 * @returns 用于避让文字笔画的间距
 */
export function getMLeaderTextGap(textHeight: number = 2.5, landingGap?: number) {
  const height = Math.max(Math.abs(textHeight), MLEADER_EPSILON)
  const requestedGap = Math.abs(landingGap ?? height * MLEADER_TEXT_GAP_FACTOR)
  const minGap = height * MLEADER_TEXT_GAP_FACTOR
  return Math.max(requestedGap, minGap)
}

/**
 * 计算着陆线终点；若提供 textPosition 且水平距离足够，则以文字位置横坐标为着陆终点。
 *
 * @param elbowPoint - 肘点坐标
 * @param options - 着陆参数，包含方向、距离、文字位置
 * @returns 着陆终点坐标
 */
export function buildMLeaderLandingEnd(
  elbowPoint: IPoint,
  options?: {
    direction?: MLeaderLandingDirection
    landingDistance?: number
    textPosition?: IPoint
  }
): IPoint {
  const direction =
    options?.direction ?? (options?.textPosition && options.textPosition.x < elbowPoint.x ? -1 : 1)
  const landingDistance = Math.max(
    Math.abs(options?.landingDistance ?? DEFAULT_MLEADER_LANDING_DISTANCE),
    1e-6
  )

  if (options?.textPosition) {
    const dx = options.textPosition.x - elbowPoint.x
    if (dx !== 0 && Math.abs(dx) >= landingDistance) {
      return {
        x: options.textPosition.x,
        y: elbowPoint.y,
      }
    }
  }

  return {
    x: elbowPoint.x + direction * landingDistance,
    y: elbowPoint.y,
  }
}

/**
 * 克隆引线分支。
 *
 * @param branch - 要克隆的引线分支
 * @returns 与入参字段一致的新分支
 */
export function cloneMLeaderBranch(branch: MLeaderBranch): MLeaderBranch {
  return {
    arrowPoint: branch.arrowPoint ? clonePoint(branch.arrowPoint) : { x: 0, y: 0 },
    vertices: (branch.vertices || []).map(clonePoint),
  }
}

/**
 * 标准化多重引线分支列表，补齐肘点与着陆终点。
 *
 * @param branches - 原始分支列表，缺省视为空数组
 * @param options - 标准化选项，含文字位置、着陆距离、是否绘制着陆线
 * @returns 标准化后的分支列表
 */
export function normalizeMLeaderBranches(
  branches?: MLeaderBranch[],
  options?: NormalizeMLeaderBranchOptions
): MLeaderBranch[] {
  return (branches || []).map((branch) => normalizeMLeaderBranch(branch, options))
}

/**
 * 获取引线分支的所有渲染点，顺序为箭头点、肘点、后续节点。
 *
 * @param branch - 引线分支
 * @returns 渲染用的点序列
 */
export function getMLeaderBranchPoints(branch: MLeaderBranch): IPoint[] {
  const points = [clonePoint(branch.arrowPoint)]
  ;(branch.vertices || []).forEach((vertex) => {
    points.push(clonePoint(vertex))
  })
  return points
}

/**
 * 获取引线分支的肘点。
 *
 * @param branch - 引线分支；缺省返回 null
 * @returns 肘点坐标；无分支时返回 null
 */
export function getMLeaderElbowPoint(branch?: MLeaderBranch): IPoint | null {
  if (!branch) {
    return null
  }
  const elbowPoint = branch.vertices[0]
  return elbowPoint ? clonePoint(elbowPoint) : clonePoint(branch.arrowPoint)
}

/**
 * 获取引线着陆终点。
 *
 * @param branch - 引线分支；缺省返回 null
 * @returns 着陆终点坐标；无分支时返回 null
 */
export function getMLeaderLandingEnd(branch?: MLeaderBranch): IPoint | null {
  if (!branch) {
    return null
  }
  if (branch.vertices[1]) {
    return clonePoint(branch.vertices[1])
  }
  return getMLeaderElbowPoint(branch)
}

/**
 * 获取引线着陆方向。
 *
 * @param branch - 引线分支；缺省返回 1
 * @param textPosition - 文字位置，用于优先判定方向
 * @returns 着陆方向，1 向右，-1 向左
 */
export function getMLeaderLandingDirection(
  branch?: MLeaderBranch,
  textPosition?: IPoint
): MLeaderLandingDirection {
  if (!branch) {
    return 1
  }
  const elbowPoint = getMLeaderElbowPoint(branch) || clonePoint(branch.arrowPoint)
  if (textPosition && textPosition.x !== elbowPoint.x) {
    return textPosition.x >= elbowPoint.x ? 1 : -1
  }
  const landingEnd = getMLeaderLandingEnd(branch)
  if (landingEnd && landingEnd.x !== elbowPoint.x) {
    return landingEnd.x >= elbowPoint.x ? 1 : -1
  }
  return resolveLandingDirection(branch.arrowPoint, elbowPoint, textPosition)
}

/**
 * 获取引线着陆距离。
 *
 * @param branch - 引线分支
 * @param fallbackDistance - 分支信息不足时使用的回退距离，单位米
 * @returns 着陆距离，单位米，至少 1e-6
 */
export function getMLeaderLandingDistance(
  branch?: MLeaderBranch,
  fallbackDistance = DEFAULT_MLEADER_LANDING_DISTANCE
) {
  const elbowPoint = getMLeaderElbowPoint(branch)
  const landingEnd = getMLeaderLandingEnd(branch)
  if (elbowPoint && landingEnd) {
    const dx = landingEnd.x - elbowPoint.x
    const dy = landingEnd.y - elbowPoint.y
    return Math.max(Math.sqrt(dx * dx + dy * dy), 1e-6)
  }
  return Math.max(Math.abs(fallbackDistance), 1e-6)
}

/**
 * 获取着陆线方向单位向量。
 *
 * @param branch - 引线分支
 * @param fallbackDirection - 分支信息不足时使用的水平方向
 * @returns 着陆线方向单位向量
 */
export function getMLeaderLandingVector(
  branch?: MLeaderBranch,
  fallbackDirection: MLeaderLandingDirection = 1
): IPoint {
  const elbowPoint = getMLeaderElbowPoint(branch)
  const landingEnd = getMLeaderLandingEnd(branch)
  if (elbowPoint && landingEnd) {
    const dx = landingEnd.x - elbowPoint.x
    const dy = landingEnd.y - elbowPoint.y
    const length = Math.sqrt(dx * dx + dy * dy)
    if (length > 1e-6) {
      return {
        x: dx / length,
        y: dy / length,
      }
    }
  }
  return { x: fallbackDirection, y: 0 }
}

/**
 * 计算多重引线文字锚点。若旧数据把文字点放在着陆线端点，则自动沿着陆方向补间距。
 *
 * @param branch - 引线分支
 * @param textPosition - 实体记录的文字点
 * @param textHeight - 文字高度
 * @param landingGap - 着陆间距
 * @returns 可直接用于渲染和导出的文字锚点
 */
export function getMLeaderTextPosition(
  branch?: MLeaderBranch,
  textPosition?: IPoint,
  textHeight?: number,
  landingGap?: number
): IPoint {
  const landingEnd = getMLeaderLandingEnd(branch)
  if (!landingEnd) {
    return textPosition ? clonePoint(textPosition) : { x: 0, y: 0 }
  }

  if (textPosition && !isSamePoint(textPosition, landingEnd)) {
    return clonePoint(textPosition)
  }

  const landingDirection = getMLeaderLandingDirection(branch, textPosition)
  const landingVector = getMLeaderLandingVector(branch, landingDirection)
  const textGap = getMLeaderTextGap(textHeight, landingGap)
  return {
    x: landingEnd.x + landingVector.x * textGap,
    y: landingEnd.y + landingVector.y * textGap,
  }
}

/**
 * 获取引线分支终点，等价于着陆终点。
 *
 * @param branch - 引线分支
 * @returns 终点坐标；无分支时返回 null
 */
export function getMLeaderBranchEnd(branch?: MLeaderBranch): IPoint | null {
  return getMLeaderLandingEnd(branch)
}

/**
 * 获取引线分支的所有线段。
 *
 * @param branch - 引线分支
 * @returns 相邻点构成的线段列表
 */
export function getMLeaderBranchLines(branch: MLeaderBranch): ILine[] {
  const points = getMLeaderBranchPoints(branch)
  const lines: ILine[] = []
  for (let i = 0; i < points.length - 1; i++) {
    lines.push({
      startPoint: clonePoint(points[i]),
      endPoint: clonePoint(points[i + 1]),
    })
  }
  return lines
}

/** 多重引线实体 */
export class MLeaderEntity extends BaseEntity {
  /** 内容承载类型，当前仅支持多行文字 */
  contentType: MLeaderContentType = 'mtext'
  /** 引线分支列表 */
  branches: MLeaderBranch[] = []
  /** 文字位置 */
  textPosition!: IPoint
  /** 文字内容 */
  contents: string = ''
  /** 文字高度，单位毫米 */
  textHeight: number = 2.5
  /** 文字旋转角度，单位弧度 */
  textRotation = 0
  /** 文字与着陆线之间的间距，单位毫米 */
  landingGap: number = 2.5
  /** 是否绘制着陆线 */
  hasLanding: boolean = true
  /** 文字位置是否被手动编辑过 */
  isTextPositionEdited: boolean = false

  protected readonly serializeKeys: string[] = [
    'contentType',
    'branches',
    'textPosition',
    'contents',
    'textHeight',
    'textRotation',
    'landingGap',
    'hasLanding',
    'isTextPositionEdited',
  ]

  constructor(data: Partial<MLeaderEntity>) {
    super(EntityType.MLeader)
    Object.assign(this, data)
    this.contentType = data.contentType || 'mtext'
    this.hasLanding = data.hasLanding ?? true
    this.textRotation = data.textRotation ?? 0
    this.textPosition = data.textPosition ? clonePoint(data.textPosition) : { x: 0, y: 0 }
    this.branches = normalizeMLeaderBranches(data.branches, {
      textPosition: data.textPosition,
      landingDistance: getDefaultMLeaderLandingDistance(
        data.textHeight ?? this.textHeight,
        data.landingGap ?? this.landingGap
      ),
      hasLanding: this.hasLanding,
    })
    const landingEnd = getMLeaderLandingEnd(this.branches[0])
    if (landingEnd) {
      this.textPosition =
        data.isTextPositionEdited && data.textPosition
          ? clonePoint(data.textPosition)
          : getMLeaderTextPosition(
              this.branches[0],
              data.textPosition,
              this.textHeight,
              this.landingGap
            )
    }
  }

  /**
   * 获取捕捉数据，返回所有分支的渲染点与线段。
   *
   * @returns 包含端点与线段的捕捉数据
   */
  getCaptureData() {
    const endPoints: IPoint[] = []
    const lines: ILine[] = []

    this.branches.forEach((branch) => {
      const branchPoints = getMLeaderBranchPoints(branch)
      endPoints.push(...branchPoints.map(clonePoint))
      lines.push(...getMLeaderBranchLines(branch))
    })

    return { endPoints, lines }
  }
}
